// ~1 cm at these latitudes — enough to absorb float noise between two exports of the same shape
// without ever merging two genuinely distinct lotes.
const COORDINATE_PRECISION = 1e7

// A closed ring needs at least 3 distinct vertices plus the repeated closing vertex.
const MIN_RING_POSITIONS = 4

const isFiniteVertex = ([lng, lat]: GeoJSON.Position): boolean =>
  Number.isFinite(lng) && Number.isFinite(lat)

const roundVertex = ([lng, lat]: GeoJSON.Position): string =>
  `${Math.round(lng * COORDINATE_PRECISION) / COORDINATE_PRECISION},${
    Math.round(lat * COORDINATE_PRECISION) / COORDINATE_PRECISION
  }`

// Booth's algorithm finds the lexicographically smallest rotation of a cyclic sequence in
// O(n); a naive "generate every rotation" scan is O(n^2) in ring length and would undercut the
// reason turf.booleanEqual was rejected in the first place. Locking onto the *first*
// occurrence of the smallest token — a plain linear scan — is not rotation-invariant when that
// token repeats: a self-touching ring exported from a different start point would then hash
// differently, which is exactly the false negative this replaces.
const leastRotationIndex = (vertices: string[]): number => {
  const doubled = [...vertices, ...vertices]
  const failure = new Array<number>(doubled.length).fill(-1)
  let candidate = 0

  for (let index = 1; index < doubled.length; index += 1) {
    const current = doubled[index]
    let matched = failure[index - candidate - 1]

    while (matched !== -1 && current !== doubled[candidate + matched + 1]) {
      if (current < doubled[candidate + matched + 1]) candidate = index - matched - 1
      matched = failure[matched]
    }

    if (current !== doubled[candidate + matched + 1]) {
      if (current < doubled[candidate + matched + 1]) candidate = index
      failure[index - candidate] = -1
    } else {
      failure[index - candidate] = matched + 1
    }
  }

  return candidate
}

const rotateToLeast = (vertices: string[]): string[] => {
  const start = leastRotationIndex(vertices)
  return [...vertices.slice(start), ...vertices.slice(0, start)]
}

// A ring is a cycle, so the same shape can be exported with a different start vertex or the
// opposite winding. Both are normalized away before hashing.
const canonicalRing = (ring: GeoJSON.Position[]): string | null => {
  if (ring.length < MIN_RING_POSITIONS || !ring.every(isFiniteVertex)) return null

  const vertices = ring.map(roundVertex)
  if (vertices.length > 1 && vertices[0] === vertices[vertices.length - 1]) vertices.pop()

  const forward = rotateToLeast(vertices).join(' ')
  const backward = rotateToLeast([...vertices].reverse()).join(' ')
  return forward <= backward ? forward : backward
}

const canonicalPolygon = (rings: GeoJSON.Position[][]): string | null => {
  if (rings.length === 0) return null

  const canonicalRings = rings.map(canonicalRing)
  if (canonicalRings.some(ring => ring === null)) return null

  // Ring order is preserved deliberately (no sorting, unlike MultiPolygon members below): two
  // otherwise-identical polygons whose holes are declared in a different order will not match.
  // That is accepted because it can only miss a duplicate, never invent one.
  return (canonicalRings as string[]).join('|')
}

const canonicalGeometry = (geometry: GeoJSON.Geometry | undefined): string | null => {
  if (geometry?.type === 'Polygon') {
    const polygon = canonicalPolygon(geometry.coordinates)
    return polygon === null ? null : `Polygon:${polygon}`
  }
  if (geometry?.type === 'MultiPolygon') {
    if (geometry.coordinates.length === 0) return null
    const members = geometry.coordinates.map(canonicalPolygon)
    if (members.some(member => member === null)) return null
    // Members are sorted so declaration order cannot make two identical multi-part lotes differ.
    return `MultiPolygon:${(members as string[]).sort().join('#')}`
  }
  return null
}

export const findDuplicateGeometries = (features: GeoJSON.Feature[]): number[][] => {
  const byGeometry = new Map<string, number[]>()

  features.forEach((feature, index) => {
    const key = canonicalGeometry(feature?.geometry)
    if (!key) return
    const group = byGeometry.get(key)
    if (group) group.push(index)
    else byGeometry.set(key, [index])
  })

  return [...byGeometry.values()].filter(group => group.length > 1)
}
